import { useState } from "react";
import styled from 'styled-components';

const content = {
  EN: {
    title: "Why Was Room2w.com Created?",
    description: `Room2w.com was born out of a need to solve one of the biggest challenges of modern work: how to connect people who need flexible workspaces with spaces that are sitting empty.
    
    Remote and mobile work is growing rapidly – but where do people actually work? Many work from home, but not everyone can or wants to do so every day. Cafés and libraries are noisy and uncomfortable, and finding a quiet space isn’t always easy. At the same time, there are millions of vacant spaces worldwide that could be put to better use.
    
    Room2w.com solves this problem by connecting people who need a workspace with those who have one available. Private individuals, businesses, and organizations can easily rent out office rooms, workspaces, and meeting rooms whenever they are not in use.`,
    
    problemTitle: "The Problem That Room2w.com Solves",
    problems: [
      {
        heading: "Millions of people need flexible workspaces",
        details: `The number of remote workers has skyrocketed. Even before the pandemic, more than 260 million people worked remotely at least part of the time – and this number has since multiplied.
        Freelancers and mobile workers need quiet and inspiring spaces without long-term rental contracts.`,
      },
      {
        heading: "Working in cafés reduces productivity",
        details: `Noisy and unergonomic environments make it difficult to focus. Studies show that disruptions can reduce productivity by up to 40%.
        Cafés don’t offer privacy or essential work tools, such as monitors, ergonomic chairs, or high-speed internet.`,
      },
      {
        heading: "Millions of square meters of space go unused",
        details: `Office spaces are, on average, 30–50% underutilized, and many cities have an abundance of vacant meeting rooms, private offices, and coworking spaces.
        Businesses pay for spaces they aren’t fully using – but they could earn money by sharing them with others.`,
      },
      {
        heading: "Climate change and efficient resource use",
        details: `Buildings consume up to 40% of the world’s energy – and a significant portion of this is wasted due to underutilization.
        By making better use of existing spaces, we can reduce the need for new office construction and lower our carbon footprint.`,
      },
    ],
    solutionTitle: "Room2w.com – A Solution That Benefits Everyone",
    solutions: [
      "For workers: Find a quiet, ergonomic, and flexible workspace near you – quickly and without long-term commitments.",
      "For space owners: Earn money from unused spaces and put them to better use.",
      "For businesses and organizations: Optimize your space, reduce costs, and support sustainability.",
      "For the environment: Less wasted space, a smaller carbon footprint, and better resource efficiency.",
    ],
  },
  FI: {
    title: "Miksi Room2w.com on perustettu?",
    description: `Room2w.com syntyi tarpeesta ratkaista yksi modernin työelämän suurimmista haasteista: miten yhdistää joustavasti työtilaa tarvitsevat ihmiset ja tyhjillään seisovat tilat.
    
    Etä- ja liikkuva työ lisääntyy vauhdilla – mutta missä sitä tehdään? Moni tekee töitä kotona, mutta kaikki eivät voi tai halua työskennellä siellä joka päivä. Kahvilat ja kirjastot ovat meluisia ja epäergonomisia, eikä rauhallista paikkaa saa aina silloin, kun sitä tarvitsee. Samalla maailmassa on valtava määrä tyhjiä tiloja, joita voitaisiin hyödyntää tehokkaammin.
    
    Room2w.com ratkaisee tämän ongelman yhdistämällä työtilaa tarvitsevat ihmiset niihin, joilla on käyttämättömiä tiloja. Yksityishenkilöt, yritykset ja yhteisöt voivat helposti vuokrata toimistohuoneita, työtiloja ja neuvotteluhuoneita silloin, kun niitä ei itse tarvita.`,
    
    problemTitle: "Ongelma, jonka Room2w.com ratkaisee",
    problems: [
      {
        heading: "Miljoonat ihmiset etsivät joustavaa työtilaa",
        details: `Etätyön määrä on kasvanut räjähdysmäisesti. Jo ennen pandemiaa yli 260 miljoonaa ihmistä teki etätöitä edes osan ajasta, ja nyt määrä on moninkertaistunut.
        Liikkuvat työntekijät ja freelancerit tarvitsevat rauhallisia ja inspiroivia työtiloja ilman pitkiä vuokrasopimuksia.`,
      },
      {
        heading: "Kahviloissa työskentely heikentää tuottavuutta",
        details: `Meluisat ja epäergonomiset ympäristöt tekevät keskittymisestä haastavaa. Tutkimukset osoittavat, että keskeytykset voivat vähentää työtehoa jopa 40 %.
        Kahvilat eivät tarjoa yksityisyyttä tai kunnollisia työvälineitä, kuten näyttöjä, ergonomisia tuoleja tai nopeaa nettiyhteyttä.`,
      },
      {
        heading: "Miljoonat neliöt tilaa seisovat tyhjillään",
        details: `Toimistotilat ovat keskimäärin 30–50 % vajaakäytöllä, ja monissa kaupungeissa on valtavasti käyttämättömiä neuvotteluhuoneita, työhuoneita ja coworking-tiloja.
        Yritykset maksavat tiloista, joita ne eivät käytä – ja voisivat samalla ansaita rahaa jakamalla ne muiden käyttöön.`,
      },
      {
        heading: "Ilmastonmuutos ja resurssien tehokas käyttö",
        details: `Rakennukset kuluttavat jopa 40 % maailman energiasta – ja suuri osa tästä menee hukkaan vajaakäytön vuoksi.
        Hyödyntämällä jo olemassa olevia tiloja tehokkaammin voimme vähentää tarvetta rakentaa uusia toimistoja ja pienentää hiilijalanjälkeä.`,
      },
    ],
    solutionTitle: "Room2w.com – ratkaisu, joka hyödyttää kaikkia",
    solutions: [
      "Työntekijöille: Löydä rauhallinen, ergonominen ja joustava työtila läheltäsi – nopeasti ja ilman sitoumuksia.",
      "Tilojen omistajille: Tienaa rahaa tyhjistä tiloista ja hyödynnä ne tehokkaammin.",
      "Yrityksille ja yhteisöille: Käytä tilasi viisaasti, vähennä kuluja ja tue kestävää kehitystä.",
      "Ympäristölle: Vähemmän hukkatilaa, pienempi hiilijalanjälki, parempi resurssien hyödyntäminen.",
    ],
  },
};

const Container = styled.div`
  display: flex;
  flex-direction: column;  /* Stack items vertically */
  align-items: center;  /* Center content horizontally */
  padding: 20px;
`;

const Content = styled.div`
  width: 60%;
  max-width: 1200px;
  background-color: #fff;
  padding: 0px;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  margin-bottom: 20px; /* Adds space between heading and content */
`;

const Heading = styled(Content)`
  font-weight: bold;
`;

const HeadingBig = styled(Content)`
  font-weight: bold;
  font-size: 30px;
`;

const List = styled(Content)`
  margin-left: 25px;
`

const WhyRoom2W = ({language}) => {

  return (
    <Container>

      <HeadingBig>{content[language].title}</HeadingBig>
      <Content>{content[language].description}</Content>

      <HeadingBig>{content[language].problemTitle}</HeadingBig>
      {content[language].problems.map((problem, index) => (
        <Content key={index}>
          <Heading>{problem.heading}</Heading>
          <p>{problem.details}</p>
        </Content>
      ))}

      <Heading>{content[language].solutionTitle}</Heading>
      
        <List>
          {content[language].solutions.map((solution, index) => (
            <li key={index}>{solution}</li>
          ))}
          </List>
    </Container>
  );
};

export default WhyRoom2W;
